// Platform identity issuer sign-in (AUTH_MODE=oidc). The SPA is a public PKCE
// OIDC client of the platform issuer: beginLogin() redirects the browser to
// /authorize; /auth/callback redeems the code at /token via completeLogin() and
// returns the issuer id_token. That token then goes through the SAME svc-02
// session exchange the Cognito flow uses (svc-02 routes on `iss`). The SPA holds
// no refresh grant, so a 1h id_token expiry means re-redirecting to /authorize.
// That does NOT re-prompt OTP on its own: the issuer plants a `__Host-pem_sso`
// cookie (Max-Age=86400) and silently mints a fresh code whenever it is present
// and `prompt !== "login"`. Ending the session therefore requires endSession()
// below — clearing local state alone leaves the issuer session live.
//
// Configuration is read lazily, at call time, through runtimeEnv(): the values
// arrive from the container's window.__ENV injection, and an environment that
// runs AUTH_MODE=cognito never sets them. Nothing here may throw at module load.

import { runtimeEnv } from "../config/runtime-env";
import { OIDC_REALM } from "../config/auth-features";
import { webCommonHost } from "../config/host";

export interface OidcConfig {
  issuer: string;
  clientId: string;
}

/** The issuer config, or null when this deployment was not given one. */
export function oidcConfig(): OidcConfig | null {
  const issuer = runtimeEnv("VITE_OIDC_ISSUER_URL")?.trim().replace(/\/+$/, "");
  const clientId = runtimeEnv("VITE_OIDC_CLIENT_ID")?.trim();
  if (!issuer || !clientId) return null;
  return { issuer, clientId };
}

function requireConfig(): OidcConfig {
  const cfg = oidcConfig();
  if (!cfg) {
    throw new Error(
      "OIDC config missing — set VITE_OIDC_ISSUER_URL and VITE_OIDC_CLIENT_ID for this deployment.",
    );
  }
  return cfg;
}

function redirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

// PKCE verifier + state ride the /authorize round-trip. They live in
// localStorage (not per-tab sessionStorage) with a short TTL + single-use
// consume, so the callback still finds them if the issuer redirect lands in a
// different tab/context. The issuer id_token shares that store (ADR-0038): it
// is what a brand-new tab re-exchanges at boot, and it is inert past its `exp`.
const PKCE_KEY = "oidc_pkce_verifier";
const STATE_KEY = "oidc_state";
const NONCE_KEY = "oidc_nonce";
const RETURN_TO_KEY = "oidc_return_to";
const FLOW_TS_KEY = "oidc_flow_ts";
const FLOW_TTL_MS = 300_000; // 5 min: an OTP round-trip, not a durable session
const ISSUER_TOKEN_KEY = "oidc_id_token";

// Silent re-auth (ISC-403) keeps its own returnTo. RETURN_TO_KEY is write-or-
// clear precisely so an abandoned interactive flow cannot capture the next
// sign-in; a silent attempt sharing it would either consume a returnTo left by
// an interactive flow or be consumed by one.
const SILENT_RETURN_TO_KEY = "oidc_silent_return_to";
// Suppression marker for the silent attempt: written BEFORE the redirect (which
// does not return, so writing it after would leave a failed attempt unrecorded)
// and cleared only once a silent sign-in has fully SUCCEEDED — meaning the
// svc-02 exchange accepted the identity, not merely that /token minted a code.
//
// That distinction is what bounds the revoked-user case. A deactivated or
// role-revoked identity still has a live SSO cookie, so the silent attempt
// keeps succeeding at the issuer and failing at svc-02. AxiosService's 401
// handler answers that with endSession(), but a BOOT-time refusal must not
// (AC4 / ADR-0038), so the SSO cookie survives and nothing else stops the
// retry. Clearing on exchange success — never on code redemption — makes the
// marker outlive that loop and hold the next boot on the passive /login.
const SILENT_MARKER_KEY = "oidc_silent_attempted_at";
// Long enough to cover a boot round-trip and an immediate reload, short enough
// that a genuinely recovered SSO session is retried rather than held out.
const SILENT_MARKER_TTL_MS = 120_000;

function flowStore(): Storage {
  return window.localStorage;
}

function base64UrlEncode(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomUrlToken(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(verifier),
  );
  return base64UrlEncode(new Uint8Array(digest));
}

/** The issuer id_token held for downstream calls (the svc-02 exchange). */
export function storedIssuerIdToken(): string | null {
  return localStorage.getItem(ISSUER_TOKEN_KEY);
}

export function clearIssuerIdToken(): void {
  localStorage.removeItem(ISSUER_TOKEN_KEY);
}

/** Adopt an issuer id_token into the shared store. */
export function setIssuerIdToken(token: string): void {
  localStorage.setItem(ISSUER_TOKEN_KEY, token);
}

/**
 * RP-initiated logout (OIDC end_session_endpoint). Drops the local issuer
 * token, then sends the browser to the issuer's /logout, which deletes the
 * server-side session, revokes its refresh-token families and clears the
 * `__Host-pem_sso` cookie. Sent WITH client_id and post_logout_redirect_uri:
 * when that URI is registered for the client the issuer 302s straight back
 * here; when it is not, the issuer renders its own signed-out page branded as
 * this client, so an unregistered value degrades rather than fails.
 *
 * The URI is per surface. The customer site browses anonymously, so signing out
 * returns to its public landing page, not to a sign-in screen the visitor did
 * not ask for. The staff surface has no anonymous view, so it keeps /login,
 * which without intent state does not auto-start a new sign-in.
 *
 * Callers MUST finish local teardown first — this is a full-page navigation and
 * does not return.
 */
// Set once endSession() has committed to the issuer /logout navigation. That
// navigation is async, so a request already in flight can 401 afterwards; its
// handler must not issue a competing assign() that would supersede /logout and
// leave the SSO cookie alive.
let loggingOut = false;

export function isLoggingOut(): boolean {
  return loggingOut;
}

export function endSession(): void {
  if (loggingOut) return;
  loggingOut = true;
  clearIssuerIdToken();
  if (typeof window !== "undefined") {
    const { issuer, clientId } = requireConfig();
    const url = new URL(`${issuer}/logout`);
    url.searchParams.set("client_id", clientId);
    const signedOutPath = webCommonHost().appContext === "web" ? "/" : "/login";
    url.searchParams.set(
      "post_logout_redirect_uri",
      `${window.location.origin}${signedOutPath}`,
    );
    window.location.assign(url.toString());
  }
}

/**
 * Redirect the browser to the issuer's /authorize (PKCE S256). Does not return.
 * `returnTo` is the in-app path the sign-in was triggered from; the callback
 * replays it through consumeReturnTo(), which is where it is guarded.
 */
export async function beginLogin(returnTo?: string): Promise<void> {
  const { issuer, params } = await startAuthorizeFlow();
  // Write or clear, never leave: a returnTo left by an abandoned flow must not
  // capture the next sign-in.
  const store = flowStore();
  if (returnTo) store.setItem(RETURN_TO_KEY, returnTo);
  else store.removeItem(RETURN_TO_KEY);
  window.location.assign(`${issuer}/authorize?${params.toString()}`);
}

/**
 * Silent re-authorisation against the live platform SSO cookie (ISC-403,
 * `prompt=none`, OIDC Core 3.1.2.6). The issuer either mints a code without
 * rendering anything, or bounces `error=login_required` back to the validated
 * redirect_uri; either way the user is never shown an issuer page. Like
 * beginLogin this is a full-page navigation and does not return.
 *
 * At boot SessionBoot holds the app on a blank frame until the restore
 * resolves, so there is no mounted UI to flash. Mid-session it is used only
 * when a 401 cannot be re-exchanged (silent-reauth.ts); the page's
 * unsaved in-memory state is lost then, which is the accepted cost. A hidden
 * iframe would avoid that, but third-party cookie policy makes it the weaker
 * carrier.
 *
 * Returns false (navigating nothing) when the attempt must not be made:
 * already suppressed by a previous failure, or a logout is in flight.
 */
export async function beginSilentLogin(returnTo?: string): Promise<boolean> {
  // endSession() is a pending full-page navigation to the issuer /logout. A
  // silent attempt racing it would supersede that navigation and re-mint the
  // session the user just ended, leaving the SSO cookie alive.
  if (loggingOut) return false;
  if (silentAttemptSuppressed()) return false;

  const store = flowStore();
  // Before the navigation, never after: assign() does not return, so a marker
  // written afterwards would never exist for a failed attempt.
  store.setItem(SILENT_MARKER_KEY, String(Date.now()));
  if (returnTo) store.setItem(SILENT_RETURN_TO_KEY, returnTo);
  else store.removeItem(SILENT_RETURN_TO_KEY);

  const { issuer, params } = await startAuthorizeFlow();
  params.set("prompt", "none");
  window.location.assign(`${issuer}/authorize?${params.toString()}`);
  return true;
}

/**
 * Shared /authorize setup: mint and stash the one-time PKCE verifier, state and
 * nonce, then build the query string. The caller owns its own returnTo key and
 * any extra params (`prompt`).
 */
async function startAuthorizeFlow(): Promise<{
  issuer: string;
  params: URLSearchParams;
}> {
  const { issuer, clientId } = requireConfig();
  const verifier = randomUrlToken(48);
  const state = randomUrlToken(24);
  const nonce = randomUrlToken(24);
  const store = flowStore();
  store.setItem(PKCE_KEY, verifier);
  store.setItem(STATE_KEY, state);
  store.setItem(NONCE_KEY, nonce);
  store.setItem(FLOW_TS_KEY, String(Date.now()));

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri(),
    response_type: "code",
    scope: "openid email profile",
    state,
    nonce,
    code_challenge: await pkceChallenge(verifier),
    code_challenge_method: "S256",
  });
  // Only when this surface has been given a realm: an unconfigured deployment
  // keeps the exact query string it sent before `realm` existed.
  if (OIDC_REALM) params.set("realm", OIDC_REALM);
  return { issuer, params };
}

/**
 * Is a silent attempt currently held out? True while a previous attempt's
 * marker is inside its TTL. A marker past its TTL is swept here, so a session
 * that has since recovered gets a fresh attempt rather than being locked out.
 */
export function silentAttemptSuppressed(): boolean {
  const store = flowStore();
  const at = Number(store.getItem(SILENT_MARKER_KEY) ?? 0);
  if (!at) return false;
  if (Date.now() - at > SILENT_MARKER_TTL_MS) {
    store.removeItem(SILENT_MARKER_KEY);
    return false;
  }
  return true;
}

/** Is this callback answering a silent attempt rather than a user's click? */
export function isSilentAttemptPending(): boolean {
  return flowStore().getItem(SILENT_MARKER_KEY) !== null;
}

/**
 * Release the suppression marker. Call ONLY once the svc-02 exchange has
 * accepted the identity: a code redeemed at /token proves the SSO cookie is
 * live, not that the identity is still authorized, and clearing on redemption
 * alone is what would let a revoked user retry forever.
 */
export function clearSilentAttemptMarker(): void {
  flowStore().removeItem(SILENT_MARKER_KEY);
}

/**
 * A recoverable sign-in failure that does NOT implicate the issuer session:
 * either the browser's own PKCE/state bookkeeping did not line up (no pending
 * flow, expired flow, state mismatch) or the code redemption at /token failed
 * (issuer unreachable/5xx/malformed body). Recovery is to restart the flow
 * in-app — never to end the issuer session, which other tabs may hold.
 */
export class LocalSignInStateError extends Error {
  readonly code:
    | "no-pending-flow"
    | "flow-expired"
    | "state-mismatch"
    | "nonce-mismatch"
    | "token-exchange-failed";
  constructor(code: LocalSignInStateError["code"], message: string) {
    super(message);
    this.name = "LocalSignInStateError";
    this.code = code;
  }
}

/**
 * Redeem the authorization code at /token. Validates state against the value
 * stashed by beginLogin (CSRF), then exchanges code + PKCE verifier for the
 * issuer id_token and checks its nonce. Stores and returns it. Consumes the
 * one-time PKCE/state/nonce.
 */
export async function completeLogin(
  code: string,
  returnedState: string,
): Promise<string> {
  const { issuer, clientId } = requireConfig();
  const store = flowStore();
  const verifier = store.getItem(PKCE_KEY);
  const expectedState = store.getItem(STATE_KEY);
  const expectedNonce = store.getItem(NONCE_KEY);
  const startedAt = Number(store.getItem(FLOW_TS_KEY) ?? 0);
  // Single-use: clear the flow keys regardless of outcome.
  store.removeItem(PKCE_KEY);
  store.removeItem(STATE_KEY);
  store.removeItem(NONCE_KEY);
  store.removeItem(FLOW_TS_KEY);
  if (!verifier || !expectedState || !expectedNonce) {
    throw new LocalSignInStateError(
      "no-pending-flow",
      "Sign-in state check failed (no pending sign-in in this browser). Please start again from Sign in.",
    );
  }
  if (startedAt && Date.now() - startedAt > FLOW_TTL_MS) {
    throw new LocalSignInStateError(
      "flow-expired",
      "Sign-in took too long and expired. Please sign in again.",
    );
  }
  if (returnedState !== expectedState) {
    throw new LocalSignInStateError(
      "state-mismatch",
      "Sign-in state check failed (state mismatch). Please sign in again.",
    );
  }

  // A /token failure does not implicate the issuer SESSION — the code redemption
  // is what failed (issuer 5xx, CORS, offline, a malformed body). Recovery is to
  // restart the flow, never to end a session other tabs may legitimately hold.
  let res: Response;
  try {
    res = await fetch(`${issuer}/token`, {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "authorization_code",
        code,
        client_id: clientId,
        redirect_uri: redirectUri(),
        code_verifier: verifier,
      }).toString(),
    });
  } catch {
    throw new LocalSignInStateError(
      "token-exchange-failed",
      "Could not reach the sign-in service. Please try again.",
    );
  }
  if (!res.ok) {
    throw new LocalSignInStateError(
      "token-exchange-failed",
      "Sign-in could not be completed. Please try again.",
    );
  }
  const body = (await res.json()) as { id_token?: string };
  if (!body.id_token) {
    throw new LocalSignInStateError(
      "token-exchange-failed",
      "Sign-in could not be completed. Please try again.",
    );
  }
  // The nonce binds this id_token to THIS browser's /authorize request. Absent
  // or mismatched means it is not the answer to our request, so it is refused
  // before the svc-02 exchange ever sees it.
  if (idTokenNonce(body.id_token) !== expectedNonce) {
    throw new LocalSignInStateError(
      "nonce-mismatch",
      "Sign-in state check failed (nonce mismatch). Please sign in again.",
    );
  }
  localStorage.setItem(ISSUER_TOKEN_KEY, body.id_token);
  return body.id_token;
}

// Unverified payload decode, as issuer-claims.ts does it: svc-02 verifies the
// signature on exchange, so this only reads back the nonce we ourselves sent.
function idTokenNonce(idToken: string): string | null {
  try {
    const [, payload] = idToken.split(".");
    if (!payload) return null;
    const claims = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    ) as Record<string, unknown>;
    return typeof claims.nonce === "string" ? claims.nonce : null;
  } catch {
    return null;
  }
}

/**
 * The in-app path this sign-in started from, consumed once. The stored value is
 * resolved against this origin and honoured only if it stays here, so any
 * absolute, protocol-relative or otherwise off-origin form falls back to "/".
 */
export function consumeReturnTo(): string {
  return consumeReturnToKey(RETURN_TO_KEY);
}

/**
 * The same, for a silent attempt's own returnTo. Separate key, identical
 * origin guard — this is what returns the user to the page they were on
 * instead of the app root.
 */
export function consumeSilentReturnTo(): string {
  return consumeReturnToKey(SILENT_RETURN_TO_KEY);
}

function consumeReturnToKey(key: string): string {
  const store = flowStore();
  const value = store.getItem(key);
  store.removeItem(key);
  if (!value) return "/";
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return "/";
    // Dot segments can leave the parsed pathname starting "//", which reads as
    // protocol-relative again; collapse the run as react-router's own does.
    return url.pathname.replace(/^\/+/, "/") + url.search + url.hash;
  } catch {
    return "/";
  }
}
